import type {Recommendation} from './Recommendation.js'
import {MergedRecommendation} from './MergedRecommendation.js'
import {groupRecommendationsByProjectId} from './grouping/groupRecommendationsByProjectId.js'
import {groupRecommendationsBySource} from './grouping/groupRecommendationsBySource.js'
import {mergeRecommendationSources} from './mergeRecommendationSources.js'
import {groupOverlappingRecommendations} from './grouping/groupOverlappingRecommendations.js'

export const mergeRecommendations = (recommendations: Recommendation[]): (MergedRecommendation | Recommendation)[] => {
  return groupRecommendationsByProjectId(recommendations).flatMap(({projectId, recommendations}) => {
    return groupRecommendationsBySource(recommendations).flatMap(({sourceType, connectionId, recommendations}) => {
      return groupOverlappingRecommendations(recommendations).map((recommendations) => {
        if (recommendations.length === 1) return recommendations[0]

        const minStart = recommendations //
          .map(({start}) => new Date(start))
          .reduce((min, start) => (!min || start.getTime() < min.getTime() ? start : min))

        const maxEnd = recommendations //
          .map(({end}) => new Date(end))
          .reduce((max, end) => (!max || end.getTime() > max.getTime() ? end : max))

        const maxBreakHours = recommendations //
          .map(({breakHours}) => breakHours)
          .reduce((max, breakHours) => (breakHours > max ? breakHours : max), 0)

        const mergedDescription = [...new Set(recommendations.map(({description}) => description))].join('\n')

        return MergedRecommendation.parse({
          id: `merged-${recommendations.map(({id}) => id).join('|')}`,
          start: minStart.toISOString(),
          end: maxEnd.toISOString(),
          breakHours: maxBreakHours,
          description: mergedDescription,
          projectId,
          source: mergeRecommendationSources(sourceType, connectionId, recommendations),
          recommendations,
        } satisfies MergedRecommendation)
      })
    })
  })
}
